import * as MatchStatus from 'customer-data-filters-ui/filterQueryFormat/MatchStatus';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useIsVisibleOnScreen } from '../../hooks/useIsVisibleOnScreen';
import { useValidateDraftCondition } from '../../hooks/useValidateDraftCondition';
import { useViewerActions } from '../../hooks/useViewerActions';
import classnames from 'classnames';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import ViewerConditionClickableBoxDeleteButton from './ViewerConditionClickableBoxDeleteButton';
import { isConditionWithErrors, isLogicGroup, isLogicGroupWithFilterValidationErrors } from '../../filterQueryFormat/logic/LogicGroup';
import { isOperator } from '../../filterQueryFormat/operator/Operator';
import ViewerConditionEditor from './ViewerConditionEditor';
import { useStrategy } from '../../hooks/useStrategy';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const WrapperComponent = styled.div.withConfig({
  displayName: "ViewerConditionClickableBox__WrapperComponent",
  componentId: "sc-97n7hv-0"
})(["&.clickable-box{line-height:22px;}.highlight-container{position:relative;", " border:1px solid ", ";border-radius:", ";", " > .condition-box{border:none;}}.condition-content-box &{.highlight-container{padding:", ";border-color:transparent;}}.delete-button-container{display:none;position:absolute;list-style:none;top:2px;right:2px;border-radius:", ";}&.highlight{.highlight-container{border-color:", ";}&[role='button']{cursor:pointer;}&&&:hover > .highlight-container{", " ", " > .delete-button-container{display:block;}}&&&.mouse-over-delete:hover > .highlight-container{border-color:", ";", "}&&&.match-pass:hover > .highlight-container{border-left:4px solid ", ";}&&&.match-fail:hover > .highlight-container{border-left:4px solid ", ";}}&.active{border-color:", ";.highlight-container{border-color:", ";background-color:", ";", "}.condition-content-box{background-color:", ";}}&.delete-highlight > .highlight-container{> .delete-button-container{display:block;}}&.delete-only.highlight{.highlight-container{", " border-color:", " !important;}}&.highlight,&.active{&.with-error{border-color:", " !important;.condition-box{", "}.highlight-container{border-color:", ";}}}&.match-pass{.highlight-container{border-left:4px solid ", ";}}&.match-fail{.highlight-container{border-left:4px solid ", ";}}"], props => !props.useOnePanel && `outline: 1px solid transparent;`, ({
  isInConditionContentBox,
  theme
}) => isInConditionContentBox ? 'transparent' : theme.color['border-core-hover'], ({
  theme
}) => theme.borderRadius[100], ({
  isInConditionContentBox,
  theme
}) => isInConditionContentBox ? `padding: ${theme.space[100]};` : undefined, ({
  theme
}) => theme.space[100], ({
  theme
}) => theme.borderRadius[100], props => props.useOnePanel ? props.theme.color['border-core-default'] : props.theme.color['fill-info-default'], ({
  useOnePanel,
  theme
}) => useOnePanel && `border-color: ${theme.color['fill-info-default']};`, ({
  useOnePanel,
  theme
}) => !useOnePanel && `outline-color: ${theme.color['fill-info-default']};`, ({
  theme
}) => theme.color['border-alert-default'], ({
  useOnePanel,
  theme
}) => !useOnePanel && `outline-color: ${theme.color['border-alert-default']};`, ({
  theme
}) => theme.color['fill-positive-default'], ({
  theme
}) => theme.color['border-alert-default'], props => props.theme.color['fill-info-default'], props => props.theme.color['fill-info-default'], ({
  theme
}) => theme.color['fill-info-subtle'], props => props.useOnePanel && `padding: 0;`, ({
  theme
}) => theme.color['fill-info-subtle'], props => !props.useOnePanel && `outline: none;`, props => props.theme.color['border-core-default'], ({
  theme
}) => theme.color['border-alert-default'], props => !props.useOnePanel && `outline: none;`, ({
  theme
}) => theme.color['border-alert-default'], ({
  theme
}) => theme.color['fill-positive-default'], ({
  theme
}) => theme.color['border-alert-default']);
const ViewerConditionClickableBoxHighlightContainer = styled.div.withConfig({
  displayName: "ViewerConditionClickableBox__ViewerConditionClickableBoxHighlightContainer",
  componentId: "sc-97n7hv-1"
})(({
  theme
}) => ({
  scrollMargin: theme.space[1400]
}));
const ViewerConditionClickableBox = props => {
  const {
    children,
    onClick,
    conditionPath,
    condition,
    className,
    filterFamily,
    deleteOnly,
    hasDeleteButton,
    testFilterData,
    use,
    isInConditionContentBox
  } = props;
  const {
    draftCondition,
    viewerConditionToDelete,
    viewerConditionToDeleteHovered,
    isInEditMode,
    props: {
      showAllFiltersInEditMode,
      useOnePanel
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    isTestFiltersEnabled
  } = useStrategy();
  const {
    onUpdateFilterSelected
  } = useViewerActions();
  const {
    validateDraftCondition
  } = useValidateDraftCondition();
  const containerRef = useRef(undefined);
  const {
    outOfViewDirection,
    isVisible
  } = useIsVisibleOnScreen(containerRef, document.querySelector('[id^="cdf-viewer-scrolling-container"]'));
  const isEditorOpen = Boolean(draftCondition);
  const isFilterBranchDeleteTriggered = !!viewerConditionToDelete && conditionPath.equals(viewerConditionToDelete);
  const hasError = useMemo(() => {
    if (condition && isOperator(condition)) {
      return isConditionWithErrors(condition);
    }
    if (condition && isLogicGroup(condition)) {
      const isLogicGroupWithErrors = condition.errors.length > 0;
      const isLogicGroupRefinementsWithErrors = !!condition.refinements && isLogicGroupWithFilterValidationErrors(condition.refinements);
      return isLogicGroupWithErrors || isLogicGroupRefinementsWithErrors;
    }
    return false;
  }, [condition]);
  const onClickHandler = evt => {
    if (isEditorOpen) {
      validateDraftCondition();
    }
    if (onClick) {
      onClick(evt);
    }
  };
  const draftConditionPath = draftCondition && draftCondition.conditionPath;
  const isAddingNewCondition = draftCondition && draftCondition.isAddingNew;
  const isOpenInEditor = showAllFiltersInEditMode ? isInEditMode : draftConditionPath && conditionPath.equals(draftConditionPath) && !isAddingNewCondition;
  const shouldHighlight = isInEditMode && !isFilterBranchDeleteTriggered;
  const isMouseOverDeleteButton = hasDeleteButton && viewerConditionToDeleteHovered && conditionPath.equals(viewerConditionToDeleteHovered);
  const editorProps = !isInEditMode || isFilterBranchDeleteTriggered || deleteOnly || useOnePanel && isOpenInEditor ? {} : {
    onClick: onClickHandler,
    role: 'button'
  };
  useEffect(() => {
    if (!useOnePanel && isOpenInEditor) {
      onUpdateFilterSelected({
        direction: outOfViewDirection,
        isFilterVisible: isVisible
      });
    }
  }, [isOpenInEditor, isVisible, outOfViewDirection, onUpdateFilterSelected, useOnePanel]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized: className, COMPLEX_TEMPLATE_EXPRESSION
    _jsx(WrapperComponent, Object.assign({
      "data-test-id": "fr-viewer-condition-box",
      className: classnames(className, 'clickable-box', useOnePanel && isTestFiltersEnabled && [(testFilterData === null || testFilterData === void 0 ? void 0 : testFilterData.status) === MatchStatus.PASS && 'match-pass', (testFilterData === null || testFilterData === void 0 ? void 0 : testFilterData.status) === MatchStatus.FAIL && 'match-fail'], shouldHighlight && 'highlight', isFilterBranchDeleteTriggered && 'delete-highlight', isOpenInEditor && 'active', (useOnePanel ? !isOpenInEditor && hasError : hasError) && 'with-error', deleteOnly && 'delete-only', isMouseOverDeleteButton && 'mouse-over-delete'),
      useOnePanel: !!useOnePanel,
      isInConditionContentBox: !!isInConditionContentBox
    }, editorProps, {
      children: /*#__PURE__*/_jsx(ViewerConditionClickableBoxHighlightContainer, {
        className: "highlight-container"
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
        ,
        ref: containerRef,
        children: useOnePanel && isOpenInEditor ? /*#__PURE__*/_jsx(ViewerConditionEditor, {
          conditionPath: conditionPath,
          filterFamily: filterFamily
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [children, hasDeleteButton &&
          /*#__PURE__*/
          // this is only for hover-able delete button
          _jsx(ViewerConditionClickableBoxDeleteButton, {
            conditionPath: conditionPath,
            isFilterBranchDeleteTriggered: isFilterBranchDeleteTriggered,
            use: use
          })]
        })
      })
    }))
  );
};
export default ViewerConditionClickableBox;